import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import "../../CSS/BeninInfo.css";
import { FaArrowUp } from "react-icons/fa";
import { FaTimes } from "react-icons/fa";
import { useLanguageData } from '../../hooks/useLanguageData';

function BeninInfo() {
  const { t } = useTranslation();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showGeographyModal, setShowGeographyModal] = useState(false);
  const [infoData, setInfoData] = useState([]);
  const [languageData, setLanguageData] = useState([]);
  const [geographyData, setGeographyData] = useState(null);
  const languageModalRef = useRef(null);
  const geographyModalRef = useRef(null);

  // Configuration des slugs pour les données principales
  const mainInfoSlugs = [
    "visit_home_section_two_1",
    "visit_home_section_two_2", 
    "visit_home_section_two_3",
    "visit_home_section_two_4",
    "visit_home_section_two_5",
    "visit_home_section_two_6",
    "visit_home_section_two_7"
  ];

  // Configuration des slugs pour les langues
  const languageSlugs = [
    "langue_benin_nord",
    "langue_benin_nord_ouest",
    "langue_benin_nord_est",
    "langue_benin_centre_sud",
    "langue_benin_centre_nord_ouest_sud",
    "langue_benin_sud"
  ];

  // Appeler tous les hooks individuellement
  const data1 = useLanguageData(`/api/v1/embassy/static-data?slug=${mainInfoSlugs[0]}`);
  const data2 = useLanguageData(`/api/v1/embassy/static-data?slug=${mainInfoSlugs[1]}`);
  const data3 = useLanguageData(`/api/v1/embassy/static-data?slug=${mainInfoSlugs[2]}`);
  const data4 = useLanguageData(`/api/v1/embassy/static-data?slug=${mainInfoSlugs[3]}`);
  const data5 = useLanguageData(`/api/v1/embassy/static-data?slug=${mainInfoSlugs[4]}`);
  const data6 = useLanguageData(`/api/v1/embassy/static-data?slug=${mainInfoSlugs[5]}`);
  const data7 = useLanguageData(`/api/v1/embassy/static-data?slug=${mainInfoSlugs[6]}`);

  // Hooks pour les langues
  const lang1 = useLanguageData(`/api/v1/embassy/static-data?slug=${languageSlugs[0]}`);
  const lang2 = useLanguageData(`/api/v1/embassy/static-data?slug=${languageSlugs[1]}`);
  const lang3 = useLanguageData(`/api/v1/embassy/static-data?slug=${languageSlugs[2]}`);
  const lang4 = useLanguageData(`/api/v1/embassy/static-data?slug=${languageSlugs[3]}`);
  const lang5 = useLanguageData(`/api/v1/embassy/static-data?slug=${languageSlugs[4]}`);
  const lang6 = useLanguageData(`/api/v1/embassy/static-data?slug=${languageSlugs[5]}`);

  // Hook pour la géographie
  const { 
    data: geographyResponse, 
    loading: geographyLoading 
  } = useLanguageData('/api/v1/embassy/static-data?slug=geographie_benin');

  const mainInfoHooks = [data1, data2, data3, data4, data5, data6, data7];
  const languageHooks = [lang1, lang2, lang3, lang4, lang5, lang6];

  // Fonction pour extraire l'URL du contenu
  const extractURLFromContent = (content) => {
    if (!content) return null;
    const urlMatch = content.match(/https?:\/\/[^\s<]+/);
    return urlMatch ? urlMatch[0].replace(/<\/?[^>]+>/g, '') : null;
  };

  // Fonction pour nettoyer le contenu (enlever les balises HTML seulement)
  const cleanHTML = (content) => {
    if (!content) return '';
    return content
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Fonction pour nettoyer le contenu ET enlever l'URL
  const cleanContentWithoutUrl = (content) => {
    if (!content) return '';
    // D'abord enlever l'URL
    let cleanText = content.replace(/https?:\/\/[^\s<]+/g, '');
    // Ensuite enlever les balises HTML
    cleanText = cleanText
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return cleanText;
  };

  // Fonction pour gérer le clic sur les cartes avec redirection
  const handleCardClick = (slug, url) => {
    if (slug === "visit_home_section_two_5" || slug === "visit_home_section_two_6") {
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } else if (slug === "visit_home_section_two_1") {
      handleLanguageClick();
    } else if (slug === "visit_home_section_two_7") {
      handleGeographyClick();
    }
  };

  // Traiter les données principales
  useEffect(() => {
    const validData = [];
    
    mainInfoHooks.forEach((hook, index) => {
      const slug = mainInfoSlugs[index];
      if (hook.data?.data && hook.data.data.length > 0) {
        const data = hook.data.data[0];
        const rawContent = data.content || '';
        
        let redirectUrl = null;
        let displayText = '';
        
        if (slug === "visit_home_section_two_5" || slug === "visit_home_section_two_6") {
          // Pour les slugs avec redirection
          redirectUrl = extractURLFromContent(rawContent);
          displayText = cleanContentWithoutUrl(rawContent);
        } else {
          // Pour les autres slugs
          displayText = cleanHTML(rawContent);
        }
        
        validData.push({
          slug: slug,
          name: data.name,
          label: data.name?.toUpperCase() || '',
          value: displayText || t('common.infoUnavailable'),
          image: data.image,
          iconClass: getIconClassBySlug(slug),
          hasArrow: slug === "visit_home_section_two_1" || slug === "visit_home_section_two_5" || slug === "visit_home_section_two_6" || slug === "visit_home_section_two_7",
          onClick: () => handleCardClick(slug, redirectUrl),
          redirectUrl: redirectUrl,
          loaded: true
        });
      }
    });

    setInfoData(validData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data1.data, data2.data, data3.data, data4.data, data5.data, data6.data, data7.data, t]);

  // Traiter les données de langue
  useEffect(() => {
    const validLanguageData = [];
    
    languageHooks.forEach((hook, index) => {
      const slug = languageSlugs[index];
      if (hook.data?.data && hook.data.data.length > 0) {
        const data = hook.data.data[0];
        validLanguageData.push({
          slug: slug,
          region: data.name,
          content: data.content,
          languages: [cleanHTML(data.content)],
          loaded: true
        });
      }
    });

    setLanguageData(validLanguageData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang1.data, lang2.data, lang3.data, lang4.data, lang5.data, lang6.data]);

  // Traiter les données de géographie
  useEffect(() => {
    if (geographyResponse?.data && geographyResponse.data.length > 0) {
      const data = geographyResponse.data[0];
      setGeographyData({
        title: data.name,
        content: data.content
      });
    }
  }, [geographyResponse]);

  const mainInfoLoading = mainInfoHooks.some(hook => hook.loading);
  const languageLoading = languageHooks.some(hook => hook.loading);
  const loading = mainInfoLoading || languageLoading || geographyLoading;

  const InfoIndisponible = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '200px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      color: '#999',
      fontStyle: 'italic',
      fontSize: '1rem',
      textAlign: 'center',
      padding: '2rem',
      margin: '1rem 0',
      width: '100%'
    }}>
      {t('common.infoUnavailable')}
    </div>
  );

  // Fermer les modals en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageModalRef.current && !languageModalRef.current.contains(event.target)) {
        setShowLanguageModal(false);
      }
      if (geographyModalRef.current && !geographyModalRef.current.contains(event.target)) {
        setShowGeographyModal(false);
      }
    };

    if (showLanguageModal || showGeographyModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageModal, showGeographyModal]);

  const getIconClassBySlug = (slug) => {
    const iconMap = {
      'visit_home_section_two_1': 'fi fi-sr-globe',
      'visit_home_section_two_2': 'fi fi-sr-marker', 
      'visit_home_section_two_3': 'fi fi-sr-users-alt',
      'visit_home_section_two_4': 'fi fi-ss-coins',
      'visit_home_section_two_5': 'fi fi-sc-passport',
      'visit_home_section_two_6': 'fi fi-bs-compass-alt',
      'visit_home_section_two_7': 'fi fi-bs-compass-alt'
    };
    return iconMap[slug] || 'fi fi-bs-compass-alt';
  };

  const handleLanguageClick = () => setShowLanguageModal(true);
  const handleGeographyClick = () => setShowGeographyModal(true);
  const handleCloseModal = () => {
    setShowLanguageModal(false);
    setShowGeographyModal(false);
  };

  const handleImageError = (e) => {
    e.target.style.display = 'none';
  };

  if (loading) {
    return (
      <div className="benin-info-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="benin-info-container">
        {infoData.map((item, index) => (
          <div 
            className="info-cadre" 
            key={index}
            onClick={item.onClick}
            style={item.onClick ? { cursor: 'pointer' } : {}}
          >
            <div className="info-cadre-content">
              <div className="info-cadre-header">
                {item.image ? (
                  <div className="info-image-container">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="info-image"
                      onError={handleImageError}
                    />
                  </div>
                ) : (
                  <i className={`info-icon ${item.iconClass}`}></i>
                )}
                <div className="info-text">
                  <p className="info-label">{item.label}</p>
                  <p className="info-value">{item.value || t('common.infoUnavailable')}</p>
                </div>
              </div>
              {item.hasArrow && (
                <FaArrowUp className="arrow-icon" />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal des langues */}
      {showLanguageModal && (
        <div className="language-modal-backdrop">
          <div className="language-modal-content" ref={languageModalRef}>
            <div className="language-modal-header">
              <h2 className="language-modal-title">{t('beninInfo.languagesTitle')}</h2>
              <button className="language-modal-close" onClick={handleCloseModal} aria-label="Fermer">
                <FaTimes />
              </button>
            </div>
            <div className="language-modal-body" style={{marginBottom:'50px'}}>
              {languageLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>{t('common.loading')}</p>
                </div>
              ) : languageData.length === 0 ? (
                <InfoIndisponible />
              ) : (
                <div className="language-grid">
                  {languageData.map((region, index) => (
                    <div key={index} className="language-region">
                      <h3 className="region-title">{region.region}</h3>
                      {region.content ? (
                        <div className="language-content" dangerouslySetInnerHTML={{ __html: region.content }} />
                      ) : (
                        <InfoIndisponible />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de géographie */}
      {showGeographyModal && (
        <div className="geography-modal-backdrop">
          <div className="geography-modal-content" ref={geographyModalRef}>
            <div className="geography-modal-header">
              <h2 className="geography-modal-title">
                {geographyData?.title || t('beninInfo.geographyTitle')}
              </h2>
              <button className="geography-modal-close" onClick={handleCloseModal} aria-label="Fermer">
                <FaTimes />
              </button>
            </div>
            <div className="geography-modal-body">
              {geographyLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>{t('common.loading')}</p>
                </div>
              ) : !geographyData ? (
                <InfoIndisponible />
              ) : (
                <div className="geography-content" dangerouslySetInnerHTML={{ __html: geographyData.content || t('common.infoUnavailable') }} />
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          text-align: center;
          min-height: 200px;
          width: 100%;
        }
        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #1a3c5e;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .info-image-container { display: flex; align-items: center; }
        .info-image { width: 40px; height: 40px; object-fit: contain; margin-right: 12px; transition: filter 0.3s ease; }
        .info-cadre:hover .info-image { filter: brightness(0) invert(1); }
        .language-modal-backdrop, .geography-modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: #0a2b57b9;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 2000;
        }
        .language-modal-content, .geography-modal-content {
          background: #e7ebf0;
          width: 90%;
          max-width: 1300px;
          height: 600px;
          border-radius: 0px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .language-modal-header, .geography-modal-header {
          background: transparent;
          color: #0a2c57;
          padding: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .language-modal-title, .geography-modal-title {
          margin: 0;
          font-size: 2.2rem;
          font-weight: 700;
          color: #0a2c57;
        }
        .language-modal-close, .geography-modal-close {
          background: none;
          border: 1px solid #0a2c57;
          color: #0a2c57;
          font-size: 1.5rem;
          cursor: pointer;
          padding: 10px;
          width: 80px;
          height: 65px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10%;
          transition: all 0.3s ease;
        }
        .language-modal-close:hover, .geography-modal-close:hover {
          background-color: #0a2c57;
          color: white;
        }
        .language-modal-body, .geography-modal-body {
          padding: 2rem;
          flex: 1;
          overflow-y: auto;
        }
        .language-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          height: 100%;
        }
        .language-region {
          background: white;
          padding: 1.5rem;
          border-radius: 0;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          position: relative;
        }
        .region-title {
          color: #0a2c57;
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 0rem;
          padding-bottom: 0.5rem;
          text-align: left;
          text-transform: uppercase;
        }
        .language-content {
          color: #0a2c57;
          font-size: 0.9rem;
          font-weight: 500;
          text-align: left;
          line-height: 1.4;
        }
        .language-content p { margin: 0; }
        .geography-content { max-width: 100%; line-height: 1.6; color: #0a2c57; }
        .geography-content p { margin-bottom: 1rem; text-align: justify; }
        .geography-content ul { margin: 1rem 0; padding-left: 1.5rem; }
        .geography-content li { margin-bottom: 0.5rem; text-align: justify; line-height: 1.2; }
        @media (max-width: 768px) {
          .language-modal-content, .geography-modal-content { width: 95%; height: 80vh; }
          .language-grid { grid-template-columns: 1fr; }
          .language-modal-title, .geography-modal-title { font-size: 1.8rem; }
          .language-modal-close, .geography-modal-close { width: 60px; height: 50px; }
          .geography-content p, .geography-content li { font-size: 0.9rem; }
          .info-image { width: 32px; height: 32px; }
        }
      `}</style>
    </>
  );
}

export default BeninInfo;